import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Undefinable } from '@symfa-inc/providence-verizon-types';
import { Col, Form, Popover, Row } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { SelectValue } from 'antd/lib/select';
import classNames from 'classnames';
import { Store } from 'rc-field-form/es/interface';
import { HttpService, ObjectComparatorService } from '@core/services';
import { SiteHttpService } from '@core/services/http';
import {
  useDidUpdateEffect,
  useFindCountyAndJurisdictions,
  useSaveChanged,
} from '@core/utils/hooks';
import {
  arrsToSelectableObject,
  formDiffs,
  isValidForm,
  normalize,
  replaceUndefinedWithNull,
  toggleModal,
} from '@core/utils/methods';
import {
  INPUT_POSITIVE_NUMBER_VALIDATOR,
  YES_NO_GROUP,
} from '@models/constants';
import { ModalMainTypes } from '@models/enums';
import { OptionProps, SiteBrowse, StateWithCounties } from '@models/interfaces';
import {
  ConfirmationPopup,
  NotificationsLoader,
  Panel,
} from '@shared/components';
import {
  Autocomplete,
  Coordinate,
  Input,
  InputNumber,
  PhoneNumber,
  PrimaryButton,
  RadioGroup,
  ShowMoreInput,
  TextArea,
} from '@shared/modules';
import { CommonActions, SiteActions } from '@store/actions';
import { SharedSelectors, SiteSelectors } from '@store/selectors';
import { SiteFormProps } from './models/interfaces';
import { DisablingModal, SiteOnSwitch } from './components';
import { SITE_UNIQUE_FIELD } from './utils';

import './styles.scss';

const { useForm } = Form;

const NORMALIZE_VALUE = (value: string) => value || null;

export const SiteInformation: FC<SiteFormProps> = ({
  isEditing,
  toggleEditing,
  isNew = false,
  className,
  permissions: {
    canRemoveSite,
    turnOnOffSite,
    siteInformationFields: {
      DNE,
      MDGLocationCode,
      PSLocationCode,
      NTPRequirement: NTPRequirementPermissions,
      accessDetails: accessDetailsPermissions,
      alternateSiteInfo,
      city,
      county,
      equipmentType,
      gateOrDoorCode: gateOrDoorCodePermissions,
      generatorInstalled,
      sqInchesEquipmentAllowed,
      sqInchesEquipmentUsed,
      sqInchesCablesAllowed,
      sqInchesCablesUsed,
      groundElevation,
      isHighRisk,
      jurisdiction,
      landlordLegalEntityName,
      latitude,
      leaseAccessRestriction: leaseAccessRestrictionPermissions,
      leasedGroundSpace,
      longitude,
      siteNote: siteNotePermissions,
      noticeComment: noticeCommentPermissions,
      isNoticeNeeded,
      parcelID,
      parcelOwner,
      primaryTech,
      primaryTechMobile,
      providenceAccessNote: providenceAccessNotePermissions,
      riskCategory,
      riskNote: riskNotePermissions,
      contractNumber,
      siteName,
      state,
      streetAddress,
      submarket,
      towerHeight,
      towerOwner,
      towerOwnerSiteID,
      towerType,
      zip,
    },
  },
  data,
  history,
}: PropsWithChildren<SiteFormProps>) => {
  const dispatch = useDispatch();

  const [form] = useForm();

  const siteService = HttpService.getHttpRequests(SiteHttpService);

  const { dropdownsOptions, states } = useSelector(
    SharedSelectors.getDropdownOptionsAndStates,
  );

  const siteFetching = useSelector(SiteSelectors.isFetching);

  const [initialForm, setInitialForm] = useState<Store>({});
  const [stateId, setStateId] = useState<Undefinable<string>>('');
  const [countyOptions, setCountyOptions] = useState<OptionProps[]>([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<OptionProps[]>(
    [],
  );
  const [disablingModalVisible, setDisablingModalVisible] =
    useState<boolean>(false);
  const [siteOnSwitch, setSiteOnSwitch] = useState<boolean>();
  const [siteOnPopconfirm, setSiteOnPopconfirm] = useState<boolean>();
  const [isRiskCategoryRequired, setRiskCategoryRequired] =
    useState<boolean>(false);
  const [areNoticeCommentsRequired, setNoticeCommentsRequired] =
    useState<boolean>(false);
  const [isMDGLocationCodeRequired, setMDGLocationCodeRequired] =
    useState<boolean>(!data || !!data?.PSLocationCode);
  const [isPSLocationCodeRequired, setPSLocationCodeRequired] =
    useState<boolean>(!data || !!data?.MDGLocationCode);

  const [objectsCompare, setObjectsCompare] = useState<boolean>();

  const resetForm = (): void => {
    setStateId(data?.state || '');

    if (isNew) {
      form.resetFields();
    } else if (data) {
      const {
        accessDetails,
        gateOrDoorCode,
        leaseAccessRestriction,
        riskNote,
        siteNote,
        noticeComment,
        providenceAccessNote,
        NTPRequirement,
        ...restData
      } = data;

      form.setFieldsValue({ ...restData, ...form.getFieldsValue() });
    } else {
      form.setFieldsValue({ ...data, ...form.getFieldsValue() });
    }
  };

  useFindCountyAndJurisdictions({
    stateId,
    setCountyOptions,
    setJurisdictionOptions,
    effectDepArray: [stateId, states],
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    if (isNew) {
      history?.push(
        `/project/create/${data?.PSLocationCode ?? data?.MDGLocationCode}`,
      );
      dispatch(SiteActions.clearCreatedSite.done());

      return;
    }

    resetForm();
  }, [data, isEditing]);

  useEffect(() => {
    if (data) {
      setMDGLocationCodeRequired(!data?.PSLocationCode);
      setPSLocationCodeRequired(!data?.MDGLocationCode);
      setSiteOnSwitch(data?.isSiteOn);
    }
  }, [data]);

  useEffect(() => {
    if (!data && isNew) {
      setInitialForm({ ...form.getFieldsValue(), longitude: undefined });
    }
  }, [isNew]);

  useEffect(
    () => (): void => {
      dispatch(CommonActions.setHasUnsubmittedData.done(false));
    },
    [],
  );

  useDidUpdateEffect(() => {
    dispatch(CommonActions.setHasUnsubmittedData.done(!objectsCompare));
  }, [objectsCompare]);

  const formatSiteForm = (): SiteBrowse => {
    const values = form.getFieldsValue();

    return formDiffs<SiteBrowse>(data ?? {}, {
      ...(data ?? {}),
      ...values,
      longitude:
        values.longitude !== '-' ? (values.longitude as number) : undefined,
    });
  };

  const onSubmit = async (): Promise<void> => {
    if (await isValidForm(form)) {
      setObjectsCompare(true);

      try {
        const formattedFormData = formatSiteForm();

        if (isNew) {
          await siteService.createSite(formattedFormData);
        } else {
          const formDataWithNull = replaceUndefinedWithNull(formattedFormData);

          await siteService.updateSite(data?.id, formDataWithNull);

          const { PSLocationCode: PSField, MDGLocationCode: MDGField } =
            formattedFormData;

          if (PSField !== undefined || MDGField !== undefined) {
            history?.replace(
              `/site/${
                form?.getFieldValue('PSLocationCode') ??
                form?.getFieldValue('MDGLocationCode')
              }/details/site-information`,
            );
          }

          toggleEditing?.();
        }

        NotificationsLoader.notificationSuccess(
          `Site has been ${isNew ? 'created' : 'updated'}!`,
        );
      } catch (e) {
        setObjectsCompare(false);
        console.error(e);
      }
    }
  };

  const onCancel = (): void => {
    resetForm();

    setObjectsCompare(true);

    toggleEditing?.();
  };

  const onDelete = async (): Promise<void> => {
    try {
      await siteService.deleteSite(data?.id);

      NotificationsLoader.notificationSuccess('Site has been deleted!');
    } catch (e) {
      console.error(e);
    }

    history?.push('/site/browse');
  };

  const changeSiteState = (
    checked: boolean,
    disablingNote?: string,
  ): Promise<SiteBrowse> =>
    siteService.updateSite(data?.id, {
      isSiteOn: checked,
      disablingNote,
    } as SiteBrowse);

  const onToggleSite = (): void => {
    setDisablingModalVisible(!disablingModalVisible);
  };

  const setDisablingNotesValue = async (value: string): Promise<void> => {
    await changeSiteState(false, value);
    onToggleSite();
  };

  const onChange = (): void => {
    const {
      latitude: initialLatitude,
      longitude: initialLongitude,
      id,
      isSiteOn,
      activeOrAuthorizedProjectsCount,
      hasActiveOrAuthorizedProjects,
      ...rest
    } = data ?? {};

    const formValues = form.getFieldsValue();

    setObjectsCompare(
      ObjectComparatorService.objectsCompare(
        {
          ...formValues,
          longitude: Number.isNaN(formValues.longitude)
            ? undefined
            : formValues.longitude,
        },
        isNew
          ? initialForm
          : {
              ...rest,

              latitude: parseFloat(initialLatitude),
              longitude: parseFloat(initialLongitude),
            },
        true,
      ),
    );
  };

  const buttonsDisabled = siteFetching || objectsCompare !== false;
  const hasActiveOrAuthorizedProjects =
    data?.hasActiveOrAuthorizedProjects ??
    !!data?.activeOrAuthorizedProjectsCount;

  const isSwitchDisabled = data?.isSiteOn && hasActiveOrAuthorizedProjects;

  useSaveChanged(isEditing, onSubmit, onCancel);

  return (
    <Form
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 14 }}
      className={classNames('prov-site-information', className)}
      form={form}
      onChange={onChange}
    >
      <DisablingModal
        visible={disablingModalVisible}
        modalType={ModalMainTypes.Add}
        toggleModal={toggleModal(setDisablingModalVisible)}
        fieldSetter={setDisablingNotesValue}
      />
      <div
        className={classNames('tabs-wrap', {
          'tabs-wrap_with-actions': isNew || isEditing,
        })}
      >
        <Panel
          header={
            <Row
              justify="space-between"
              className="prov-site-information__header"
            >
              <Col className="header-title">Site Information</Col>
              {!isNew && turnOnOffSite && (
                <Col
                  span={6}
                  className={classNames('prov-site-information__info', {
                    'disabled-mode': !isSwitchDisabled,
                  })}
                >
                  <Popover
                    content={
                      <ConfirmationPopup
                        onOk={(): void => {
                          changeSiteState(true);
                          setSiteOnPopconfirm(false);
                        }}
                        onCancel={(): void => setSiteOnPopconfirm(false)}
                        text="Are you sure you want to reactivate this site?"
                      />
                    }
                    placement="top"
                    visible={!disablingModalVisible && siteOnPopconfirm}
                  >
                    <SiteOnSwitch
                      onChange={(checked: boolean): void => {
                        const callback = checked
                          ? setSiteOnPopconfirm
                          : onToggleSite;

                        callback(checked);
                      }}
                      switchState={siteOnSwitch}
                      disabled={isSwitchDisabled}
                    />
                  </Popover>
                </Col>
              )}
            </Row>
          }
          className="prov-site-information__panel"
        >
          <Row>
            <Col span="8">
              <Input
                id="MDGLocationCode"
                label="MDG Location Code"
                elementProps={{
                  maxLength: 10,
                  minLength: 10,
                  onChange: async e => {
                    setPSLocationCodeRequired(!e.target.value);
                    onChange();

                    await isValidForm(form);
                  },
                }}
                isEditing={isEditing && !!MDGLocationCode}
                formItemProps={{
                  normalize: NORMALIZE_VALUE,
                  rules: SITE_UNIQUE_FIELD(
                    isMDGLocationCodeRequired,
                    'MDG Location Code',
                    10,
                    data?.id,
                  ),
                }}
              />
              <Input
                id="PSLocationCode"
                label="PS Location Code"
                elementProps={{
                  maxLength: 6,
                  minLength: 6,
                  onChange: async e => {
                    setMDGLocationCodeRequired(!e.target.value);
                    onChange();

                    await isValidForm(form);
                  },
                }}
                isEditing={isEditing && !!PSLocationCode}
                formItemProps={{
                  normalize: NORMALIZE_VALUE,
                  rules: SITE_UNIQUE_FIELD(
                    isPSLocationCodeRequired,
                    'PS Location Code',
                    6,
                    data?.id,
                  ),
                }}
              />
              <Input
                id="siteName"
                label="Site Name"
                isEditing={isEditing && !!siteName}
              />
              <Input
                id="contractNumber"
                label="Contract Number"
                isEditing={isEditing && !!contractNumber}
              />
              <Input
                id="alternateSiteInfo"
                label="Alternate Site Info"
                isEditing={isEditing && !!alternateSiteInfo}
              />
              <Autocomplete
                id="towerOwner"
                label="Tower Owner"
                isEditing={isEditing && !!towerOwner}
                options={dropdownsOptions?.TowerOwner || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Input
                id="towerOwnerSiteID"
                label="Tower Owner Site ID"
                isEditing={isEditing && !!towerOwnerSiteID}
              />
              <Input
                id="parcelID"
                label="Parcel ID"
                isEditing={isEditing && !!parcelID}
              />
              <Input
                id="parcelOwner"
                label="Parcel Owner"
                isEditing={isEditing && !!parcelOwner}
              />
              <Input
                id="landlordLegalEntityName"
                label="Landlord Legal Entity Name"
                isEditing={isEditing && !!landlordLegalEntityName}
              />
              <Input
                id="streetAddress"
                label="Street Address"
                isEditing={isEditing && !!streetAddress}
              />
              <Input id="city" label="City" isEditing={isEditing && !!city} />
              <Autocomplete
                id="state"
                label="State"
                options={arrsToSelectableObject<StateWithCounties>(
                  states,
                  'id',
                  'name',
                )}
                elementProps={{
                  onChange: (value: SelectValue): void => {
                    form.setFieldsValue({
                      county: null,
                      jurisdiction: null,
                    });
                    setStateId(value as string);
                    onChange();
                  },
                  allowClear: true,
                }}
                isEditing={isEditing && !!state}
              />
              <InputNumber
                id="zip"
                label="Zip"
                isEditing={isEditing && !!zip}
                formItemProps={{
                  normalize,
                  rules: [INPUT_POSITIVE_NUMBER_VALIDATOR],
                }}
                elementProps={{ onChange }}
              />
              <Autocomplete
                id="county"
                label="County"
                isEditing={isEditing && !!county}
                options={stateId ? countyOptions : []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Autocomplete
                id="jurisdiction"
                label="Jurisdiction"
                isEditing={isEditing && !!jurisdiction}
                options={stateId ? jurisdictionOptions : []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Coordinate
                id="latitude"
                label="Latitude"
                isEditing={isEditing && !!latitude}
              />
              <Coordinate
                id="longitude"
                label="Longitude"
                isEditing={isEditing && !!longitude}
              />
              <Autocomplete
                id="submarket"
                label="Submarket"
                options={dropdownsOptions?.Submarket || []}
                isEditing={isEditing && !!submarket}
                elementProps={{ onChange, allowClear: true }}
              />
            </Col>
            <Col span="8">
              <Autocomplete
                id="towerType"
                label="Tower Type"
                isEditing={isEditing && !!towerType}
                options={dropdownsOptions?.TowerType || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <InputNumber
                id="towerHeight"
                label="Tower Height"
                isEditing={isEditing && !!towerHeight}
                formItemProps={{
                  normalize,
                  rules: [INPUT_POSITIVE_NUMBER_VALIDATOR],
                }}
                elementProps={{ onChange }}
              />
              <InputNumber
                id="DNE"
                label="DNE"
                isEditing={isEditing && !!DNE}
                formItemProps={{
                  normalize,
                  rules: [INPUT_POSITIVE_NUMBER_VALIDATOR],
                }}
                elementProps={{ onChange }}
              />
              <InputNumber
                id="groundElevation"
                label="Ground Elevation"
                isEditing={isEditing && !!groundElevation}
                formItemProps={{
                  normalize,
                }}
                elementProps={{ onChange }}
              />
              <Input
                id="leasedGroundSpace"
                label="Leased Ground Space"
                isEditing={isEditing && !!leasedGroundSpace}
              />
              <Autocomplete
                id="equipmentType"
                label="Equipment Type"
                isEditing={isEditing && !!equipmentType}
                options={dropdownsOptions?.EquipmentType || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Autocomplete
                id="generatorInstalled"
                label="Generator Installed"
                isEditing={isEditing && !!generatorInstalled}
                options={dropdownsOptions?.GeneratorInstalled || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Input
                id="sqInchesEquipmentAllowed"
                label="Sq. Inches Equipment Allowed"
                isEditing={isEditing && !!sqInchesEquipmentAllowed}
              />
              <Input
                id="sqInchesEquipmentUsed"
                label="Sq. Inches Equipment Used"
                isEditing={isEditing && !!sqInchesEquipmentUsed}
              />
              <Input
                id="sqInchesCablesAllowed"
                label="Sq. Inches Cables Allowed"
                isEditing={isEditing && !!sqInchesCablesAllowed}
              />
              <Input
                id="sqInchesCablesUsed"
                label="Sq. Inches Cables Used"
                isEditing={isEditing && !!sqInchesCablesUsed}
              />
              <ShowMoreInput
                id="NTPRequirement"
                label="NTP Requirements"
                data={data?.NTPRequirement}
                form={form}
                isEditing={isEditing && !!NTPRequirementPermissions}
              />
              <ShowMoreInput
                id="siteNote"
                label="Site Notes"
                data={data?.siteNote}
                form={form}
                isEditing={isEditing && !!siteNotePermissions}
              />
              {!isNew && !data?.isSiteOn && (
                <TextArea
                  id="disablingNote"
                  label="Disabling notes"
                  isEditing={false}
                />
              )}
            </Col>
            <Col span="8">
              <RadioGroup
                id="isHighRisk"
                label="High Risk"
                buttons={YES_NO_GROUP}
                isEditing={isEditing && !!isHighRisk}
                elementProps={{
                  onChange: (event: RadioChangeEvent): void =>
                    setRiskCategoryRequired(event.target.value),
                }}
              />
              <Autocomplete
                id="riskCategory"
                label="Risk Category"
                options={dropdownsOptions?.RiskCategory || []}
                isEditing={isEditing && !!riskCategory}
                formItemProps={{
                  rules: [
                    {
                      required: isRiskCategoryRequired,
                      message: 'Risk Category is required!',
                    },
                  ],
                }}
                elementProps={{ onChange, allowClear: true }}
              />
              <ShowMoreInput
                id="riskNote"
                label="Risk Note"
                data={data?.riskNote}
                form={form}
                isEditing={isEditing && !!riskNotePermissions}
                formItemProps={{
                  rules: [
                    {
                      required: isRiskCategoryRequired,
                      message: 'Risk Note is required!',
                    },
                  ],
                }}
              />
              <ShowMoreInput
                id="providenceAccessNote"
                label="Providence Access Notes"
                data={data?.providenceAccessNote}
                form={form}
                isEditing={isEditing && !!providenceAccessNotePermissions}
              />
              <ShowMoreInput
                id="accessDetails"
                label="Access Details"
                data={data?.accessDetails}
                form={form}
                isEditing={isEditing && !!accessDetailsPermissions}
              />
              <ShowMoreInput
                id="gateOrDoorCode"
                label="Gate/Door Codes"
                data={data?.gateOrDoorCode}
                form={form}
                isEditing={isEditing && !!gateOrDoorCodePermissions}
              />
              <RadioGroup
                id="isNoticeNeeded"
                label="Notice Needed"
                buttons={YES_NO_GROUP}
                isEditing={isEditing && !!isNoticeNeeded}
                elementProps={{
                  onChange: (e: RadioChangeEvent) => {
                    setNoticeCommentsRequired(e.target.value);
                  },
                }}
              />
              <ShowMoreInput
                id="noticeComment"
                label="Notice Comments"
                data={data?.noticeComment}
                form={form}
                isEditing={isEditing && !!noticeCommentPermissions}
                formItemProps={{
                  rules: [
                    {
                      required: areNoticeCommentsRequired,
                      message: 'Notice Comments are required!',
                    },
                  ],
                }}
              />
              <ShowMoreInput
                id="leaseAccessRestriction"
                label="Lease Access Restrictions"
                data={data?.leaseAccessRestriction}
                form={form}
                isEditing={isEditing && !!leaseAccessRestrictionPermissions}
              />
              <Input
                id="primaryTech"
                label="Primary Tech"
                isEditing={isEditing && !!primaryTech}
              />
              <PhoneNumber
                id="primaryTechMobile"
                label="Primary Tech Mobile"
                isEditing={isEditing && !!primaryTechMobile}
              />
            </Col>
          </Row>
        </Panel>
      </div>
      {isEditing && (
        <Row className="prov-site-information__btn-wrap">
          <div>
            <PrimaryButton
              htmlType="submit"
              title="Submit"
              disabled={buttonsDisabled}
              onClick={onSubmit}
            />
            <PrimaryButton
              title="Cancel"
              disabled={buttonsDisabled}
              type="default"
              onClick={onCancel}
            />
          </div>
          {!isNew && canRemoveSite && (
            <PrimaryButton
              className="site-delete"
              title="Delete this site"
              type="danger"
              onClick={onDelete}
            />
          )}
        </Row>
      )}
    </Form>
  );
};
